import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Empty, Input, Row, Col, Space, Popconfirm } from 'antd';
import { ReloadOutlined, DollarOutlined, CloseCircleOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { formatDate, formatMontant } from '../../variables/constants';
import {
  listerCheques, encaisserCheque, annulerCheque, STATUTS_CHEQUE_ATT,
} from '../../services/attestationsService';
import TableExportButton from '../../components/TableExportButton';

import { useTranslation } from 'react-i18next';

const { Search } = Input;

export default function ChequesTab({ onOpenCommande, refreshKey, actif }) {
  const { t } = useTranslation('attestations');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [acting, setActing] = useState(false);
  const [internalKey, setInternalKey] = useState(0);

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const res = await listerCheques(page, 20);
      setData(res.items);
      setPagination(prev => ({ ...prev, current: res.currentPage + 1, pageSize: res.pageSize, total: res.totalElements }));
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load, refreshKey, internalKey]);

  const filtered = data.filter(c =>
    !search ||
    c.numeroCheque?.toLowerCase().includes(search.toLowerCase()) ||
    c.numeroCommande?.toLowerCase().includes(search.toLowerCase()) ||
    c.organismeRaisonSociale?.toLowerCase().includes(search.toLowerCase())
  );

  const handle = async (fn, msg) => {
    setActing(true);
    try { await fn(); toast.success(msg); setInternalKey(k => k + 1); }
    catch (_) { /* intercepteur */ }
    finally { setActing(false); }
  };

  const columns = [
    { title: t('commun.colonnes.numeroCheque'), dataIndex: 'numeroCheque', key: 'num', width: 130, fixed: 'left',
      render: (v) => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700 }}>{v}</span> },
    { title: t('commun.colonnes.commande'), dataIndex: 'numeroCommande', key: 'cmd', width: 140,
      render: (v, r) => v ? (
        <button onClick={() => onOpenCommande?.(r.commandeTrackingId)}
          style={{ background: 'none', border: 'none', color: '#1a6b3c', fontWeight: 600,
            fontFamily: 'var(--font-main)', cursor: 'pointer', padding: 0, fontSize: 12 }}>
          {v}
        </button>
      ) : '-' },
    { title: t('commun.colonnes.compagnie'), dataIndex: 'organismeRaisonSociale', key: 'org', ellipsis: true },
    { title: t('commun.colonnes.banque'), dataIndex: 'banqueEmettrice', key: 'banq', ellipsis: true,
      render: (v) => v || '-' },
    { title: t('cheques.colonnes.encaisse'), dataIndex: 'dateEncaissement', key: 'enc', width: 100, format: 'date',
      render: (v) => v ? formatDate(v) : '-' },
    { title: t('commun.colonnes.montant'), dataIndex: 'montant', key: 'mt', width: 130, align: 'right', format: 'currency', numeric: true,
      render: (v) => <strong style={{ color: '#1a6b3c' }}>{formatMontant(v)}</strong> },
    { title: t('commun.colonnes.statut'), dataIndex: 'statut', key: 'st', width: 130,
      export: (v) => (STATUTS_CHEQUE_ATT[v]?.label) || v || '',
      render: (v) => {
        const s = STATUTS_CHEQUE_ATT[v] || { label: v, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      } },
    { title: '', key: 'act', width: 200, fixed: 'right', exportable: false,
      render: (_, c) => (
        <Space size={4}>
          {c.statut === 'RECU' && (
            <Popconfirm title={t('commun.cheque.encaisserTitre')}
              description={t('commun.cheque.encaisserDescription')}
              onConfirm={() => handle(() => encaisserCheque(c.chequeTrackingId, dayjs().format('YYYY-MM-DD')),
                t('commun.cheque.encaisse'))}>
              <Button size="small" type="primary" icon={<DollarOutlined />} loading={acting}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>{t('commun.cheque.encaisser')}</Button>
            </Popconfirm>
          )}
          {(c.statut === 'RECU' || c.statut === 'ENCAISSE') && (
            <Popconfirm title={t('commun.cheque.annulerTitre')}
              onConfirm={() => handle(() => annulerCheque(c.chequeTrackingId, 'Annulation manuelle'),
                t('commun.cheque.annule'))}>
              <Button size="small" danger icon={<CloseCircleOutlined />} loading={acting}>{t('commun.annuler')}</Button>
            </Popconfirm>
          )}
          {c.commandeTrackingId && (
            <Button size="small" icon={<EyeOutlined />}
              onClick={() => onOpenCommande?.(c.commandeTrackingId)}>{t('cheques.voirCommande')}</Button>
          )}
        </Space>
      ) },
  ];

  return (
    <>
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={24} md={16}>
          <Search placeholder={t('cheques.recherche')} value={search}
            onChange={(e) => setSearch(e.target.value)} allowClear />
        </Col>
        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => load(pagination.current - 1)}>{t('commun.actualiser')}</Button>
          </Space>
        </Col>
      </Row>

      <Table className="cb-table" dataSource={filtered} columns={columns} rowKey="chequeTrackingId"
        loading={loading} scroll={{ x: 1180 }}
        pagination={{
          current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
          onChange: (page) => load(page - 1),
          showTotal: (total) => t('cheques.total', { total }),
        }}
        locale={{ emptyText: <Empty description={t('commun.aucunCheque')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />

      {/*  Export flottant  */}
      {/* RENDU SEULEMENT SI CET ONGLET EST ACTIF. Les quatre onglets de l'ecran
          portent chacun leur export ; AntD garde montes les volets deja visites, et
          quatre boutons flottants se seraient superposes au meme coin. La condition
          est ce qui garantit qu'il n'y en a qu'un.

          `floating` suffit au placement : le composant pose lui-meme l'enveloppe et
          la reserve de place en bas de page. */}
      {actif && (
        <TableExportButton
          data={filtered}
          columns={columns}
          filename="attestations-cheques"
          title={t('cheques.exportTitre')}
          subtitle={search ? t('commun.recherche', { search }) : undefined}
          floating
        />
      )}
    </>
  );
}
